<script setup lang="ts">
/**
 * Page affichée à la place du formulaire de don/billetterie/adhésion
 * quand l'asso a un risk_status qui empêche les paiements.
 *
 * Couvre tous les états (suspended, restricted, documents_requested, banned).
 * Page complète avec hero, message clair, et CTA "Retour à l'accueil".
 */

type RiskStatus =
  | "clean"
  | "documents_requested"
  | "watch"
  | "restricted"
  | "suspended"
  | "banned";

const props = defineProps<{
  organizationName?: string | null;
  riskStatus: RiskStatus;
  reason?: string | null;
}>();

const { t } = useI18n();

const variantConfig = computed(() => {
  switch (props.riskStatus) {
    case "banned":
      return {
        icon: "i-lucide-ban",
        iconColor: "text-red-600 dark:text-red-400",
        iconBg: "bg-red-100 dark:bg-red-500/15",
        title: t("association.indisponible.banniTitre"),
        defaultMessage: t("association.indisponible.banniTexte"),
      };
    case "suspended":
      return {
        icon: "i-lucide-pause-circle",
        iconColor: "text-red-600 dark:text-red-400",
        iconBg: "bg-red-100 dark:bg-red-500/15",
        title: t("association.indisponible.suspendueTitre"),
        defaultMessage: t("association.indisponible.suspendueTexte"),
      };
    case "restricted":
      return {
        icon: "i-lucide-shield-alert",
        iconColor: "text-orange-600 dark:text-orange-400",
        iconBg: "bg-orange-100 dark:bg-orange-500/15",
        title: t("association.indisponible.restreintTitre"),
        defaultMessage: t("association.indisponible.restreintTexte"),
      };
    case "documents_requested":
      return {
        icon: "i-lucide-clock",
        iconColor: "text-blue-600 dark:text-blue-400",
        iconBg: "bg-blue-100 dark:bg-blue-500/15",
        title: t("association.indisponible.verificationTitre"),
        defaultMessage: t("association.indisponible.verificationTexte"),
      };
    case "watch":
      return {
        icon: "i-lucide-info",
        iconColor: "text-amber-600 dark:text-amber-400",
        iconBg: "bg-amber-100 dark:bg-amber-500/15",
        title: t("association.indisponible.routineTitre"),
        defaultMessage: t("association.indisponible.routineTexte"),
      };
    default:
      return {
        icon: "i-lucide-info",
        iconColor: "text-neutral-500",
        iconBg: "bg-neutral-100 dark:bg-white/5",
        title: t("association.indisponible.informationTitre"),
        defaultMessage: "",
      };
  }
});

const finalMessage = computed(
  () => props.reason || variantConfig.value.defaultMessage,
);
</script>

<template>
  <div class="min-h-[60vh] flex items-center justify-center px-4 py-16">
    <!-- `max-w-md` étranglait la rangée de boutons : « Retour à l'accueil » et
         « Découvrir d'autres associations » passaient sur deux lignes chacun.
         Le texte reste borné à max-w-md (confort de lecture), seuls les
         boutons peuvent s'étaler. -->
    <div class="max-w-xl w-full text-center">
      <div
        class="size-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
        :class="variantConfig.iconBg"
      >
        <UIcon :name="variantConfig.icon" class="size-8" :class="variantConfig.iconColor" />
      </div>

      <h1 class="text-2xl md:text-3xl font-semibold text-neutral-900 dark:text-white tracking-tight">
        {{ variantConfig.title }}
      </h1>

      <p v-if="organizationName" class="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
        {{ organizationName }}
      </p>

      <p class="mt-4 max-w-md mx-auto text-base text-neutral-700 dark:text-neutral-300 leading-relaxed">
        {{ finalMessage }}
      </p>

      <!-- `whitespace-nowrap` : les libellés tiennent sur une seule ligne. En
           dessous de sm on empile, donc pas de débordement horizontal. -->
      <div class="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <NuxtLink
          to="/"
          class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-neutral-900 dark:bg-white px-5 py-3 text-sm font-medium text-white dark:text-neutral-900 hover:opacity-90 transition-opacity"
        >
          <UIcon name="i-lucide-arrow-left" class="size-4 shrink-0" />
          {{ t("association.indisponible.retourAccueil") }}
        </NuxtLink>
        <NuxtLink
          to="/recherche"
          class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-neutral-200 dark:border-white/10 px-5 py-3 text-sm font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-white/5 transition-colors"
        >
          <UIcon name="i-lucide-search" class="size-4 shrink-0" />
          {{ t("association.indisponible.decouvrirAutres") }}
        </NuxtLink>
      </div>
    </div>
  </div>
</template>
